import React, { useCallback, useState } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { WorkflowStatus } from '@/services/api/generated/firmsService';

interface TextFieldProps {
  label: string;
  description?: string | null;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  status?: WorkflowStatus;
  placeholder?: string;
  multiline?: boolean;
}

/**
 * Generic text field renderer with explicit save/cancel.
 * Supports both single-line (input) and multiline (textarea) modes.
 * Follows the NotesEditor pattern - edit mode with Save/Cancel buttons.
 */
export const TextField: React.FC<TextFieldProps> = ({
  label,
  description,
  value,
  onChange,
  disabled = false,
  isLoading = false,
  status,
  placeholder = '',
  multiline = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = useCallback(() => {
    setLocalValue(value ?? '');
    setIsEditing(true);
  }, [value]);

  const cancelEditing = useCallback(() => {
    setLocalValue(value ?? '');
    setIsEditing(false);
  }, [value]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      onChange(localValue);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [localValue, onChange]);

  const inputClasses =
    'w-full px-2 py-1 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  const isFieldDisabled = disabled || isLoading || isSaving;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-medium text-zinc-950 dark:text-white">
          {label}
        </div>
        {status === 'coming_soon' && (
          <Badge color="zinc" className="text-[10px] py-0 px-1">
            Not Live
          </Badge>
        )}
      </div>
      {description && (
        <div className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400">
          {description}
        </div>
      )}

      {isEditing ? (
        <div className="space-y-1">
          {multiline ? (
            <textarea
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              disabled={isFieldDisabled}
              placeholder={placeholder}
              rows={3}
              className={`${inputClasses} resize-y min-h-[60px]`}
            />
          ) : (
            <input
              type="text"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              disabled={isFieldDisabled}
              placeholder={placeholder}
              className={inputClasses}
            />
          )}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={isFieldDisabled}
              className="px-1.5 py-0.5 text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={cancelEditing}
              disabled={isSaving}
              className="px-1.5 py-0.5 text-[11px] font-medium text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {value ? (
            <p className="text-[11px] text-zinc-600 dark:text-zinc-400 flex-1">
              {value}
            </p>
          ) : (
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 italic flex-1">
              Not set
            </p>
          )}
          <button
            type="button"
            onClick={startEditing}
            disabled={disabled || isLoading}
            className="px-1.5 py-0.5 text-[11px] font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
};
